import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import gold from 'asset/img/gold.svg';
import regret from 'asset/img/regret.png';
import { playSlotSpinSound, playSlotWinSound, stopDetailSuccSound } from 'utils/soundEffect';
import { useLocation } from 'react-router-dom';
import { getPetAvatarSrc } from '../../pages/Pet/petPhaseConfig';
import './index.scss';

const Wheel = React.memo((props) => {
    const { onOk, visible, skill, setWheelVisible, pet, fromYingshi, levelIndex } = props;
    const timersRef = useRef([]);
    const isMountedRef = useRef(true);
    const prizeRef = useRef(null);
    const location = useLocation();

    // P0优化：获取当前悟空头像
    const getCurrentPetAvatar = useCallback(() => {
        return getPetAvatarSrc(pet?.garden_info?.PetInfo?.Phase);
    }, [pet]);

    const { type, index, level } = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return {
            type: Number(searchParams.get('type')),
            index: Number(searchParams.get('index')),
            level: Number(searchParams.get('level')),
        };
    }, [location.search]);

    const skill1Level = skill?.[1]?.level || 0;
    const skill2Level = skill?.[2]?.level || 0;
    const coinBase = skill1Level * 2;
    const luckyBase = skill2Level * 0.5;
    // 影视入口无 type/index，用 fromYingshi + levelIndex 判断是否“第一关”
    const isFirst = fromYingshi ? Number(levelIndex) === 1 : (type === 0 && index === 0 && level === 1);

    const prizeList = useMemo(() => {
        const prize0 = coinBase + 3;
        const prize1 = coinBase + 5;
        const prize4 = coinBase + 7 + skill2Level;
        const prize7 = coinBase + 15 + skill2Level;
        return [
            { coin: prize0, label: `${prize0}枚`, weight: 1 },
            { coin: prize1, label: `${prize1}枚`, weight: 2 },
            { coin: prize0, label: `${prize0}枚`, weight: 2 },
            { coin: 0, label: '谢谢惠顾', weight: 0.3 },
            { coin: prize4, label: `${prize4}枚`, weight: luckyBase + skill2Level + 2 },
            { coin: prize0, label: `${prize0}枚`, weight: 1 },
            { coin: prize1, label: `${prize1}枚`, weight: 1 },
            { coin: prize7, label: `${prize7}枚`, weight: isFirst ? 100000000 : luckyBase + skill2Level + 1 },
        ];
    }, [coinBase, skill2Level, luckyBase, isFirst]);

    const pickPrizeIndex = useCallback(() => {
        const weights = prizeList.map((p) => Math.max(0, Number(p.weight) || 0));
        const total = weights.reduce((sum, w) => sum + w, 0);
        if (total <= 0) return 0;
        const r = Math.random() * total;
        let acc = 0;
        for (let i = 0; i < weights.length; i++) {
            acc += weights[i];
            if (r <= acc) return i;
        }
        return weights.length - 1;
    }, [prizeList]);

    // 状态：idle -> spinning -> result
    const [phase, setPhase] = useState('idle');
    const [displayPrize, setDisplayPrize] = useState(null);
    // 滚动数字动画
    const [rollingNumber, setRollingNumber] = useState(0);

    const cleanup = useCallback(() => {
        timersRef.current.forEach(t => clearTimeout(t));
        timersRef.current = [];
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            cleanup();
            prizeRef.current = null;
        };
    }, [cleanup]);

    useEffect(() => {
        if (!visible) {
            cleanup();
            setPhase('idle');
            setDisplayPrize(null);
            setRollingNumber(0);
            prizeRef.current = null;
        }
    }, [visible, cleanup]);

    const startSpin = useCallback(() => {
        if (phase !== 'idle') return;
        stopDetailSuccSound(); // 用户点击开始抽奖时再中断闯关成功音
        // 决定结果
        const prizeIndex = pickPrizeIndex();
        const prize = prizeList[prizeIndex];
        prizeRef.current = { ...prize };
        setDisplayPrize({ ...prize });
        
        setPhase('spinning');
        playSlotSpinSound();

        // 数字滚动动画
        let count = 0;
        const maxCount = 20;
        const interval = setInterval(() => {
            if (!isMountedRef.current) {
                clearInterval(interval);
                return;
            }
            count++;
            // 随机显示数字
            setRollingNumber(Math.floor(Math.random() * 30) + 1);
            
            if (count >= maxCount) {
                clearInterval(interval);
                // 显示最终结果
                setRollingNumber(prize.coin);
                setPhase('result');
                playSlotWinSound();
                
                // 延迟关闭
                const timer = setTimeout(() => {
                    if (!isMountedRef.current) return;
                    if (typeof setWheelVisible === 'function') setWheelVisible(false);
                    if (typeof onOk === 'function') onOk(prize.coin);
                }, 1500);
                timersRef.current.push(timer);
            }
        }, 100);
        
        timersRef.current.push(interval);
    }, [phase, onOk, pickPrizeIndex, prizeList, setWheelVisible]);

    if (!visible) return null;

    return (
        <div className="slot-container">
            <div className="slot-overlay" />
            <div className="slot-machine simple">
                <div className="slot-header">
                    <span className="slot-title">🎰 幸运抽奖 🎰</span>
                </div>
                
                <div className="slot-body">
                    <div className="prize-display">
                        <img 
                            src={phase === 'result' && displayPrize?.coin === 0 ? regret : gold} 
                            alt="" 
                            className={`prize-icon ${phase === 'spinning' ? 'spinning' : ''} ${phase === 'result' ? 'win' : ''}`}
                        />
                        <div className={`prize-number ${phase === 'spinning' ? 'rolling' : ''} ${phase === 'result' ? 'final' : ''}`}>
                            {phase === 'idle' && '?'}
                            {phase === 'spinning' && rollingNumber}
                            {phase === 'result' && (displayPrize?.coin > 0 ? displayPrize.coin : '0')}
                        </div>
                        <div className="prize-unit">
                            {phase === 'result' && displayPrize?.coin > 0 && '枚金币'}
                            {phase === 'result' && displayPrize?.coin === 0 && '谢谢惠顾'}
                        </div>
                    </div>
                    
                    {phase === 'result' && (
                        <div className="result-message">
                            {displayPrize?.coin > 0 ? (
                                <div className="lottery-result-text">
                                    <div className="lottery-result-title">恭喜获得 {displayPrize.coin} 枚金币！</div>
                                </div>
                            ) : (
                                <div className="lottery-result-no-prize">
                                    <div>😅 再接再厉！</div>
                                    <div className="lottery-encourage">下次会更幸运！</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <button 
                    className={`slot-button ${phase !== 'idle' ? 'disabled' : ''}`}
                    onClick={startSpin}
                    disabled={phase !== 'idle'}
                >
                    {phase === 'idle' && '🎲 开始抽奖'}
                    {phase === 'spinning' && '抽奖中...'}
                    {phase === 'result' && '抽奖完成'}
                </button>
            </div>
        </div>
    );
});

export default Wheel;
